import { useId, useState, type InputHTMLAttributes } from 'react'
import { FIELD } from './DashUI'

/**
 * A password field with the eye that unmasks it.
 *
 * 🔴 SEVEN SCREENS ASK FOR A PASSWORD AND NONE OF THEM LET ANYBODY SEE WHAT
 * THEY TYPED. Two of those screens ask twice — a password and its confirmation —
 * so the only way to find a typo was to be told the two did not match and start
 * both again. On the operator and school tiers that is an annoyance; on
 * `KidSignIn` it is a five-year-old on a shared tablet who cannot read back
 * their own dots.
 *
 * ⚠️ IT STARTS MASKED, ALWAYS, AND NOTHING PERSISTS THE REVEAL. The toggle is
 * for the moment of typing, not a preference: a field that remembered "shown"
 * would put the next person's password on the screen of a tablet a whole class
 * shares. `KidSignIn`'s own note about `autoComplete="new-password"` is the same
 * argument about the same device.
 *
 * ⚠️ THE EYE IS DRAWN HERE RATHER THAN IMPORTED. `shared/ui/icons.tsx` is
 * GENERATED from the delivered design file and `npm run icons:check` fails on
 * drift, so a 21st icon cannot be added to it by hand. The geometry below is the
 * set's — 24px grid, 2px round-cap strokes, `currentColor` — so it reads as one
 * of them; if an eye is ever added to the design source, delete these two and
 * import it.
 */
const EyeIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
)

const EyeOffIcon = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z"></path>
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M4 20 20 4"></path>
  </svg>
)

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  /**
   * The field's own class. Defaults to the dashboard field style; the kid shell
   * passes `KID_FIELD`, which is twice the height and centres its text.
   */
  className?: string
  /**
   * `kid` draws a 48px target with a 26px eye — `settings-and-controls.md`'s
   * floor for a control a child presses, and the same reason `KID_FIELD` is
   * `h-16` while the adult field is `h-11`.
   */
  tier?: 'dash' | 'kid'
}

export function PasswordInput({
  className = `${FIELD} text-dash-sm`,
  tier = 'dash',
  ...rest
}: PasswordInputProps) {
  const [shown, setShown] = useState(false)
  /*
   * ⚠️ THE HINT IS A SIBLING OF THE INPUT, NOT THE BUTTON'S LABEL. A screen
   * reader on the FIELD needs to know the text is visible — that is a fact about
   * the field, and someone who never reaches the button never hears it
   * otherwise. The button gets its own label below saying what pressing it does.
   */
  const stateId = useId()
  const kid = tier === 'kid'

  return (
    <div className="relative">
      <input
        {...rest}
        type={shown ? 'text' : 'password'}
        aria-describedby={shown ? stateId : undefined}
        /*
         * ⚠️ THE PADDING IS ON THE INPUT, NOT A MARGIN ON THE BUTTON. Without it
         * a long password runs under the eye — and on the kid field, which
         * centres its text, the reserved room has to be symmetrical or the value
         * sits visibly off-centre.
         */
        className={`${className} ${kid ? 'px-14' : 'pr-11'}`}
      />
      <button
        type="button"
        onClick={() => {
          setShown((was) => !was)
        }}
        aria-label={shown ? 'Hide the password' : 'Show the password'}
        /*
         * 🔴 `tap-exempt` IS NOT USED HERE AND THE HEIGHT IS NOT `h-full` BY
         * ACCIDENT. Matching the field's own height gives the adult field a
         * 44px target and the kid field a 64px one, which is what a control
         * sitting inside a text field has to be to beat the field's own tap.
         */
        className={`absolute top-0 right-0 flex h-full items-center justify-center text-muted transition-colors duration-150 ease-dash hover:text-ink focus-visible:text-ink ${
          kid ? 'w-14 rounded-r-xl' : 'w-11 rounded-r-lg'
        }`}
      >
        {shown ? <EyeOffIcon size={kid ? 26 : 18} /> : <EyeIcon size={kid ? 26 : 18} />}
      </button>
      {shown ? (
        <span id={stateId} className="sr-only">
          The password is showing on screen.
        </span>
      ) : null}
    </div>
  )
}

export default PasswordInput
